import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/shadcn/button"
import { Badge } from "@/components/ui/shadcn/badge"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/shadcn/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/shadcn/popover"

interface ComboboxOption {
  value: string
  label: string
  color?: string
  badge?: string
}

const getBadgeVariant = (badge: string) => {
  switch (badge) {
    case 'completed-project':
      return { variant: 'default' as const, className: 'bg-success-subtle text-success-subtle-foreground' };
    case 'active-project':
      return { variant: 'secondary' as const, className: 'bg-warning-subtle text-warning-subtle-foreground' };
    case 'project':
      return { variant: 'default' as const, className: 'bg-cat-purple text-cat-purple-foreground' };
    default:
      return { variant: 'secondary' as const, className: '' };
  }
};

interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  className?: string
  disabled?: boolean
  allowClear?: boolean
  clearText?: string
  clearClassName?: string
  /** When true, options are hidden until the user types; filtering is substring match on label and value. */
  requireSearchQuery?: boolean
  /** Shown above the list while the search box is empty (only with requireSearchQuery). */
  typeToSearchHint?: string
  /** Merged into CommandList (e.g. max height). */
  listClassName?: string
  /** Called when the search query changes. */
  onSearchChange?: (value: string) => void
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Select option...",
  searchPlaceholder = "Search...",
  emptyText = "No option found.",
  className,
  disabled = false,
  allowClear = false,
  clearText = "No selection",
  clearClassName,
  requireSearchQuery = false,
  typeToSearchHint,
  listClassName,
  onSearchChange,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')

  React.useEffect(() => {
    onSearchChange?.(searchQuery)
  }, [onSearchChange, searchQuery])

  React.useEffect(() => {
    if (!open) {
      setSearchQuery('')
    }
  }, [open])

  const selectedOption = options.find((option) => option.value === value)

  const filteredOptions = React.useMemo(() => {
    if (!requireSearchQuery) {
      return options
    }
    const q = searchQuery.trim().toLowerCase()
    if (!q) {
      return []
    }
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(q) || String(option.value).toLowerCase().includes(q),
    )
  }, [options, requireSearchQuery, searchQuery])

  const optionsToRender = requireSearchQuery ? filteredOptions : options

  const handleValueChange = (newValue: string) => {
    onValueChange?.(newValue)
    setOpen(false)
  }

  return (
    // modal: inside a Dialog or Drawer the rest of the page is inert, so the
    // popover has to install its own pointer-events guard to stay clickable.
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          <span className="truncate">
            {selectedOption ? (
              <div className="flex items-center gap-2">
                {selectedOption.color && (
                  <div 
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: selectedOption.color }}
                  />
                )}
                {selectedOption.label}
                {selectedOption.badge && (() => {
                  const badgeConfig = getBadgeVariant(selectedOption.badge);
                  return (
                    <Badge variant={badgeConfig.variant} className={`text-xs ${badgeConfig.className}`}>
                      {selectedOption.badge.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  );
                })()}
              </div>
            ) : (
              placeholder
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onWheel={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={requireSearchQuery ? false : undefined}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList className={listClassName}>
            {requireSearchQuery && searchQuery.trim() === '' && typeToSearchHint ? (
              <div
                role="status"
                className="text-muted-foreground px-2 py-2 text-center text-xs"
              >
                {typeToSearchHint}
              </div>
            ) : null}
            {requireSearchQuery && searchQuery.trim() !== '' && filteredOptions.length === 0 ? (
              <div className="text-muted-foreground py-6 text-center text-sm">{emptyText}</div>
            ) : null}
            {!requireSearchQuery ? <CommandEmpty>{emptyText}</CommandEmpty> : null}
            <CommandGroup>
              {allowClear && (
                <CommandItem
                  key="clear-option"
                  value={clearText}
                  keywords={[String(clearText), "none", "no", "clear", "unassigned"]}
                  onSelect={() => handleValueChange("")}
                  className={cn('hover:bg-accent cursor-pointer', clearClassName)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === "" ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {clearText}
                </CommandItem>
              )}
              {optionsToRender.map((option, index) => {
                // Use a unique value for CommandItem to handle duplicate labels
                // The value is used for Command's internal matching, so it must be unique
                // We combine label with value to ensure uniqueness while maintaining searchability
                const uniqueValue = `${option.label}::${option.value}::${index}`;
                return (
                <CommandItem
                  key={`${option.value}-${index}`}
                  value={uniqueValue}
                  keywords={[String(option.label), String(option.value)]}
                  onSelect={() => handleValueChange(option.value)}
                  className={'hover:bg-accent cursor-pointer'}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex items-center gap-2">
                    {option.color && (
                      <div 
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: option.color }}
                      />
                    )}
                    {option.label}
                    {option.badge && (() => {
                      const badgeConfig = getBadgeVariant(option.badge);
                      return (
                        <Badge variant={badgeConfig.variant} className={`text-xs ${badgeConfig.className}`}>
                          {option.badge.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Badge>
                      );
                    })()}
                  </div>
                </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
